import { FieldTypes } from '../../constants'
import { Field, FieldMap, ListField } from '../../types/forms/fields'
import { getBooleanHistoryField } from '../shared'
import { addCondition } from '../utils'
import { getDetailsField, getYesNoUnknownField } from './shared'

const getAllergyField = (
  id: string,
  substanceName: string,
  placeholder: string,
  conditionalOn?: string,
  withSpecifyField?: boolean,
): FieldMap => {
  const children: Record<string, Field> = {
    hasAllergy: getBooleanHistoryField(placeholder),
  }

  if (withSpecifyField) {
    children.specify = {
      type: FieldTypes.TEXT,
      placeholder: 'Please specify',
      condition: v => !!v?.hasAllergy,
    }
  }

  children.anaphylaxis = addCondition(
    getYesNoUnknownField(`Do you have an anaphylactic reaction to ${substanceName}?`),
    v => !!v?.hasAllergy,
  )
  children.details = addCondition(
    getDetailsField(
      id,
      `Please describe your reaction to ${substanceName}. List any related medications or treatments.`,
    ),
    v => !!v?.hasAllergy,
  )

  const fieldMap: FieldMap = {
    name: placeholder,
    children,
    ...(conditionalOn ? { condition: v => !!(v as Record<string, unknown>)?.[conditionalOn] } : {}),
  }

  return fieldMap
}

const medicationAllergyListField: ListField = {
  name: 'Medication Allergies',
  itemName: 'Medication',
  condition: v => !!v?.hasMedicationAllergies,
  itemFields: {
    name: 'Medication',
    children: {
      name: {
        type: FieldTypes.TEXT,
        placeholder: 'Medication name',
      },
      anaphylaxis: getYesNoUnknownField('Do you have an anaphylactic reaction to this medication?'),
      details: {
        type: FieldTypes.TEXTAREA,
        placeholder:
          'Please describe your reaction to this medication. List any related medications or treatments.',
      },
    },
  },
}

export const allergiesField: FieldMap = {
  name: 'Allergies',
  children: {
    food: {
      name: 'Food Allergies',
      children: {
        hasFoodAllergies: getBooleanHistoryField('Do you have any food allergies?'),
        egg: getAllergyField('egg', 'egg', 'Egg allergy', 'hasFoodAllergies'),
        'tree-nuts': getAllergyField(
          'tree-nuts',
          'tree nuts',
          'Tree nut allergy',
          'hasFoodAllergies',
        ),
        peanuts: getAllergyField('peanuts', 'peanuts', 'Peanut allergy', 'hasFoodAllergies'),
        soy: getAllergyField('soy', 'soy', 'Soy allergy', 'hasFoodAllergies'),
        fish: getAllergyField('fish', 'fish', 'Fish allergy', 'hasFoodAllergies'),
        shellfish: getAllergyField(
          'shellfish',
          'shellfish',
          'Shellfish allergy',
          'hasFoodAllergies',
        ),
        'other-food': getAllergyField(
          'other-food',
          'this food',
          'Other food allergy',
          'hasFoodAllergies',
          true,
        ),
      },
    },
    enviromental: {
      name: 'Environmental Allergies',
      children: {
        hasEnvironmentalAllergies: getBooleanHistoryField(
          'Do you have any environmental allergies?',
        ),
        'grass-hay': getAllergyField(
          'grass-hay',
          'Grass/Hay',
          'Grass/Hay allergy',
          'hasEnvironmentalAllergies',
        ),
        mold: getAllergyField('mold', 'Mold', 'Mold allergy', 'hasEnvironmentalAllergies'),
        pollen: getAllergyField('pollen', 'Pollen', 'Pollen allergy', 'hasEnvironmentalAllergies'),
        ragweed: getAllergyField(
          'ragweed',
          'Ragweed',
          'Ragweed allergy',
          'hasEnvironmentalAllergies',
        ),
        flowers: getAllergyField(
          'flowers',
          'Flowers',
          'Flowers allergy',
          'hasEnvironmentalAllergies',
        ),
        'dust-or-dust-mites': getAllergyField(
          'dust-or-dust-mites',
          'Dust/dust mites',
          'Dust/dust mites allergy',
          'hasEnvironmentalAllergies',
        ),
        'other-environmental': getAllergyField(
          'other-environmental',
          'this environmental factor',
          'Are you allergic to any other environmental factors?',
          'hasEnvironmentalAllergies',
          true,
        ),
      },
    },
    other: {
      name: 'Other Allergies',
      children: {
        latex: getAllergyField('latex', 'latex', 'Latex'),
        adhesives: getAllergyField(
          'adhesives',
          'adhesives and/or bandaids',
          'Adhesives and/or bandaids',
        ),
        medications: {
          name: 'Medications',
          children: {
            hasMedicationAllergies: getBooleanHistoryField('Are you allergic to any medications?'),
            medicationsList: medicationAllergyListField,
          },
        },
        other: getAllergyField(
          'other',
          'this substance',
          'Are you allergic to any other substances?',
          undefined,
          true,
        ),
      },
    },
  },
}
